import React, { useState, useContext, useEffect } from 'react'
import collapse from '../../../Assets/Images/collapse.png'
import expand from '../../../Assets/Images/expand.png'
import grow from "../../../Assets/Images/growpkg.svg";
import down from "../../../Assets/Images/downpkg2.svg";
import leaderIcon from '../../../Assets/Images/leader.svg';
import parishionerIcon from '../../../Assets/Images/parishioner.svg';
import './MatrixCompare.scss';
import LanguageContext from '../../../Store/LanguageContext';
import MultipleAssessmentsContext from '../../../Store/MultipleAssessmentsContext';
import { translate } from '../../translate/translate';
import { preventWidow } from '../../../utils/widowFix';
import { formatLeadingOutOfATeam } from '../../../utils/helper';

const ProgressBar = ({ value, color }) => (
  <div className="progress-bar-container">
    <div className="progress-bar">
      <div
        className="progress-fill"
        style={{
          width: `${value}%`,
          backgroundColor: color
        }}
      />
    </div>
  </div>
)

const MatrixCompare = ({ collapsed = false, title = "Experience of the Holy Spirit", questionsPair = null }) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [isMobile, setIsMobile] = useState(false);
  const langCtx = useContext(LanguageContext);
  useEffect(() => {
    const updateDeviceState = () => {
      if (typeof window === 'undefined') return;
      setIsMobile(window.innerWidth <= 540);
    };
    updateDeviceState();
    window.addEventListener('resize', updateDeviceState);
    return () => window.removeEventListener('resize', updateDeviceState);
  }, []);
  const multipleAssessmentsCtx = useContext(MultipleAssessmentsContext);
  const parishInsights = multipleAssessmentsCtx?.parishInsights;

  const handleTranslate = (text, type) => {
    switch (langCtx.language) {
      case "Nederlands": {
        return translate(text, "dashboardAnswersDutch", type)
      }
      default: {
        return text;
      }
    }
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Map UI section titles to API response keys
  const mapTitleToSectionKey = (t) => {
    const normalized = (t || '').toLowerCase();
    if (normalized.includes('experience of the holy spirit')) return 'Experience of the Holy Spirit';
    if (normalized.includes('asking') && normalized.includes('discerning')) return 'Asking & Discerning in Faith';
    if (normalized.includes('responding in faith')) return 'Responding in Faith';
    // Evangelization sections
    if (normalized.includes('resources aligned')) return 'Resources Aligned';
    if (normalized.includes('primacy of programs')) return 'Primacy of Programs';
    if (normalized.includes('cultural reinforcement')) return 'Cultural Reinforcement';
    // Leadership sections
    if (normalized === 'vision' || normalized.includes('vision')) return 'Vision';
    if (normalized.includes('planning') && normalized.includes('priorities')) return 'Planning and Priorities';
    if (normalized.includes('leading out of a team')) return 'Leading out of a team';
    if (normalized.includes('growing leadership skills')) return 'Growing Leadership Skills';
    return null;
  };

  // Helper to extract questions for a section from a single deployment
  const extractQuestionsForSection = (deployment, sectionKey) => {
    const areas = deployment?.areasOfExcellence || [];
    const sectionObj = areas.find((o) => Object.keys(o || {})[0] === sectionKey);
    const sectionArr = sectionObj ? sectionObj[sectionKey] : [];
    const block = Array.isArray(sectionArr) ? sectionArr[0] : null;
    const questions = block?.questions || [];
    return questions;
  }

  // Build dynamic rows from parishInsights or provided questionsPair
  const buildRowsFromParishInsights = () => {
    try {
      let q1 = [];
      let q2 = [];

      if (questionsPair && Array.isArray(questionsPair) && questionsPair.length === 2) {
        q1 = Array.isArray(questionsPair[0]) ? questionsPair[0] : [];
        q2 = Array.isArray(questionsPair[1]) ? questionsPair[1] : [];
      } else {
        if (!Array.isArray(parishInsights) || parishInsights.length < 2) return null;
        const sectionKey = mapTitleToSectionKey(title);
        if (!sectionKey) return null;
        const d1 = parishInsights[0];
        const d2 = parishInsights[1];
        q1 = extractQuestionsForSection(d1, sectionKey);
        q2 = extractQuestionsForSection(d2, sectionKey);
      }

      // Index by questionId for alignment
      const byId1 = new Map(q1.map(q => [q.questionId, q]));
      const ids = Array.from(new Set([...q1.map(q => q.questionId), ...q2.map(q => q.questionId)]));

      const rows = ids.map((id) => {
        const a = byId1.get(id);
        const b = (q2 || []).find(x => x.questionId === id);
        const questionText = a?.question || b?.question || '';
        const avg1 = a ? Number(a.totalAverage) || 0 : 0;
        const avg2 = b ? Number(b.totalAverage) || 0 : 0;
        const leaderAvg1 = a ? Number(a.leaderAverage) || 0 : 0;
        const parishAvg1 = a ? Number(a.parishionerAverage) || 0 : 0;
        const leaderAvg2 = b ? Number(b.leaderAverage) || 0 : 0;
        const parishAvg2 = b ? Number(b.parishionerAverage) || 0 : 0;
        const pctChange = avg2 - avg1;
        const direction = avg2 >= avg1 ? 'up' : 'down';

        return {
          questionId: id,
          question: questionText,
          avg1: Math.round(avg1),
          avg2: Math.round(avg2),
          leaderAvg1: Math.round(leaderAvg1),
          parishAvg1: Math.round(parishAvg1),
          leaderAvg2: Math.round(leaderAvg2),
          parishAvg2: Math.round(parishAvg2),
          percentageChange: Math.round(pctChange),
          changeDirection: direction,
        };
      });

      return rows;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const dynamicRows = buildRowsFromParishInsights();

  const getCompareAnswerDiv = (userTypeScore, id, options = {}) => {
    const { bare = false, extraStyle = {} } = options;
    const negativeQuestions = [1126, 1102, 1106, 1112];
    const roundedValue = Math.round((userTypeScore / 25) + 1);

    const cellStyle = { fontWeight: "700", ...extraStyle };
    const getContent = (text, color) => {
      const style = { ...cellStyle, color };
      if (bare) {
        return (
          <span className='mc-answer-text' style={style}>
            {text}
          </span>
        );
      }
      return (
        <div className='mc-b-cell mc-answer-text' style={style}>
          {text}
        </div>
      );
    };

    if (roundedValue == 1) {
      const label = negativeQuestions.includes(id) ? `${handleTranslate("Strongly Agree", "strongly-agree")}` : `${handleTranslate("Strongly Disagree", "strongly-disagree")}`;
      return getContent(label, "#84161D");
    }
    else if (roundedValue == 2) {
      const label = negativeQuestions.includes(id) ? `${handleTranslate("Agree", "agree")}` : `${handleTranslate("Disagree", "disagree")}`;
      return getContent(label, "#D55D53");
    }
    else if (roundedValue == 3) {
      const label = `${handleTranslate("Neutral", "neutral")}`;
      return getContent(label, "#DFB047");
    }
    else if (roundedValue == 4) {
      const label = negativeQuestions.includes(id) ? `${handleTranslate("Disagree", "disagree")}` : `${handleTranslate("Agree", "agree")}`;
      return getContent(label, "#9BB675");
    }
    else if (roundedValue == 5) {
      const label = negativeQuestions.includes(id) ? `${handleTranslate("Strongly Disagree", "strongly-disagree")}` : `${handleTranslate("Strongly Agree", "strongly-agree")}`;
      return getContent(label, "#87976E");
    }
    else {
      return getContent("N/A", "#C65B5B");
    }
  };

  const getCompletionScoreLeader = (questions) => {
    let answer = 0;
    questions?.forEach(element => {
      answer += (element?.averages?.leaders) * 20
    });
    answer /= questions?.length
    return Math.round(answer)
  }

  const getCompletionScoreParishioners = (questions) => {
    let answer = 0;
    questions?.forEach(element => {
      answer += (element?.averages?.parishioners) * 20
    });
    answer /= questions?.length
    return Math.round(answer)
  }

  const getBarColor = (complete) => {
    let color = '#87976E';
    if (complete <= 66) {
      color = '#84161D';
    } else if (complete <= 84) {
      color = '#DFB047';
    }
    return color;
  }

  const getAnswersAccordingToTab = (item) => {
    if (!dynamicRows) {
      return null;
    }

    // Get leader and parishioner counts from parishInsights
    const leaderCount1 = parishInsights?.[0]?.leaderCount ?? 0;
    const parishionerCount1 = parishInsights?.[0]?.parishionerCount ?? 0;
    const leaderCount2 = parishInsights?.[1]?.leaderCount ?? 0;
    const parishionerCount2 = parishInsights?.[1]?.parishionerCount ?? 0;

    const renderStackedAnswers = (leaderScore, parishScore, leaderCount, parishionerCount) => (
      <div className='mc-b-cell mc-b-cell-stack'>
        <div className='mc-b-subcell'>
          {/* <span className='mc-b-subcell-label'>{handleTranslate("Leaders", "leaders-label")}</span> */}
          {leaderCount === 0 
            ? <span className='mc-answer-text' style={{ fontWeight: "700", color: "#C65B5B", display: "block", textAlign: "center", width: "100%" }}>-</span>
            : getCompareAnswerDiv(leaderScore ?? 0, item?.questionId, { bare: true })}
        </div>
        <div className='mc-b-subcell'>
          {/* <span className='mc-b-subcell-label'>{handleTranslate("Parishioners", "parishioners-label")}</span> */}
          {parishionerCount === 0 
            ? <span className='mc-answer-text' style={{ fontWeight: "700", color: "#C65B5B", display: "block", textAlign: "center", width: "100%" }}>-</span>
            : getCompareAnswerDiv(parishScore ?? 0, item?.questionId, { bare: true })}
        </div>
      </div>
    );

    const leader1 = item?.leaderAvg1 ?? item?.avg1 ?? 0;
    const parish1 = item?.parishAvg1 ?? item?.avg1 ?? 0;
    const leader2 = item?.leaderAvg2 ?? item?.avg2 ?? 0;
    const parish2 = item?.parishAvg2 ?? item?.avg2 ?? 0;

    return (
      <>
        {renderStackedAnswers(leader1, parish1, leaderCount1, parishionerCount1)}
        {renderStackedAnswers(leader2, parish2, leaderCount2, parishionerCount2)}
      </>
    );
  }

  const getPercentageChangeDiv = (item) => {
    const color = "#000000";
    const arrow = item?.changeDirection === "up" ? <img src={grow} alt="up" style={{ width: '16px', height: '16px', marginLeft: '4px' }} /> : <img src={down} alt="down" style={{ width: '16px', height: '16px', marginLeft: '4px' }} />;

    return (
      <div className='mc-b-cell' style={{
        fontWeight: "700",
        color: color,
        justifyContent: 'flex-start',
        alignItems: 'center'
      }}>
        {Math.abs(item?.percentageChange)}% {arrow}
      </div>
    );
  }

  const getTranslatedQuestion = (questionText, questionId) => {
    // If English is selected, always show the base question text
    if (langCtx?.language === 'English') {
      return questionText;
    }

    // Prefer data-driven translations using the provided questionsPair
    let candidate = null;
    if (questionsPair && Array.isArray(questionsPair) && questionsPair.length === 2) {
      candidate = (questionsPair[0] || []).find(q => q?.questionId === questionId) ||
                  (questionsPair[1] || []).find(q => q?.questionId === questionId);
    } else if (Array.isArray(parishInsights) && parishInsights.length >= 2) {
      const sectionKey = mapTitleToSectionKey(title);
      if (sectionKey) {
        const q1 = extractQuestionsForSection(parishInsights[0], sectionKey);
        const q2 = extractQuestionsForSection(parishInsights[1], sectionKey);
        candidate = (q1 || []).find(q => q?.questionId === questionId) || (q2 || []).find(q => q?.questionId === questionId);
      }
    }

    if (candidate?.languages && Array.isArray(candidate.languages)) {
      // Try exact match, then Dutch for Nederlands, then first available (mirrors Matrix.jsx behavior)
      const desired = langCtx?.language;
      const alias = desired === 'Nederlands' ? 'Dutch' : desired;
      const selectedLanguage = candidate.languages.find(el => el?.language === desired)
        || candidate.languages.find(el => el?.language === alias)
        || candidate.languages[0];

      if (selectedLanguage?.answers && Array.isArray(selectedLanguage.answers)) {
        return selectedLanguage.answers[0]?.question || questionText;
      }
    }

    // Fallback: return original text
    return questionText;
  }

  return (
    <div className={`mc-matrix mc-green ${isCollapsed ? 'mc-collapsed' : 'mc-collapse'}`}>
      <div className='mc-head'>
        <div style={{ borderRadius: `${isCollapsed ? '10px' : '10px 10px 0 0'}` }} className='mc-row mc-insights'>
          <div className='mc-h-cell'>
            <p style={{ margin: "0" }}>{formatLeadingOutOfATeam(title)}</p>
          </div>
          <div className='mc-h-cell mc-role-header'></div>
          <div className='mc-h-cell min-width-120'>{(() => {
            const rows = dynamicRows;
            if (rows && rows.length) {
              const avg = Math.round(rows.reduce((s, r) => s + r.avg1, 0) / rows.length);
              return <ProgressBar value={avg} color={getBarColor(avg)} />
            }
            const v = getCompletionScoreLeader([]);
            return <ProgressBar value={v} color={getBarColor(v)} />
          })()}</div>
          <div className='mc-h-cell min-width-120'>{(() => {
            const rows = dynamicRows;
            if (rows && rows.length) {
              const avg = Math.round(rows.reduce((s, r) => s + r.avg2, 0) / rows.length);
              return <ProgressBar value={avg} color={getBarColor(avg)} />
            }
            const v = getCompletionScoreParishioners([]);
            return <ProgressBar value={v} color={getBarColor(v)} />
          })()}</div>
          <div className='mc-h-cell' style={{ textAlign: 'center' }}>{(() => {
            const rows = dynamicRows;
            if (rows && rows.length) {
              const avgPercentageChange = Math.round(rows.reduce((s, r) => s + (r.percentageChange), 0) / rows.length);
              const arrow = avgPercentageChange >= 0 ? (
                <img src={grow} alt="up" style={{ width: '16px', height: '16px', marginLeft: '4px' }} />
              ) : (
                <img src={down} alt="down" style={{ width: '16px', height: '16px', marginLeft: '4px' }} />
              );
              return (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                  {Math.abs(avgPercentageChange)}% {arrow}
                </span>
              );
            }
            return '0%';
          })()}</div>
          <div className='mc-h-cell mc-collapse' onClick={toggleCollapse}>
            <img className='mc-collapse-img' src={isCollapsed ? expand : collapse} style={{ cursor: 'pointer' }} />
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <div className='mc-body' style={{ display: "flex", flexDirection: "column", padding: "12px 0", gap: "12px" }}>
          {(dynamicRows || []).length ? dynamicRows.map((item, index) => {
            return <div className='mc-row' key={index}>
              <div className='mc-b-cell'>{isMobile ? getTranslatedQuestion(item.question, item.questionId) : preventWidow(getTranslatedQuestion(item.question, item.questionId))}</div>
              <div className='icone-container'>
                <img src={leaderIcon} alt="leader" className='mc-role-icon-one' />
                <img src={parishionerIcon} alt="parishioner" className='mc-role-icon-two' />
              </div>
              {getAnswersAccordingToTab(item)}
              {getPercentageChangeDiv(item)}
              <div className='mc-b-cell'></div>
            </div>
          }) : null}
        </div>
      )}
    </div>
  )
}

export default MatrixCompare